import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { AppLayout, DonationButton, useAuth } from '@scoring/frontend-shared';
import type { ApiClient } from '@scoring/frontend-shared';
import { encodeTeamCheckInQR } from '@scoring/shared';
import { useApiClient } from '../hooks/useApiClient.js';

/**
 * API response shape for the participant's account info.
 * Uses only the fields we need for the dashboard display.
 */
interface AccountInfo {
  name: string;
  email: string;
}

/**
 * API response shape for an event the participant is registered in.
 * Matches the `/account/events` response structure.
 */
interface ParticipantEvent {
  eventId: string;
  eventName: string;
  eventDate: string;
  eventStatus: string;
  teamName: string | null;
  eventScopedTeamId: string | null;
}

const QR_CACHE_KEY = 'participant_team_qr';

/**
 * Participant dashboard — landing page after login.
 * Displays participant name, current event info, team check-in QR code,
 * and navigation links to other pages.
 *
 * Requirements:
 *   - 15.1: participant dashboard showing event info and scores access
 *   - 13.5: team check-in QR code encoding the team's event-scoped ID
 *   - 13.6: display the team's check-in QR so any team member can show it
 *   - 18.1: cache QR code locally for offline display
 */
export function DashboardPage(): JSX.Element {
  const { user } = useAuth();
  const api = useApiClient();

  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [currentEvent, setCurrentEvent] = useState<ParticipantEvent | null>(null);
  const [qrValue, setQrValue] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchDashboardData(client: ApiClient): Promise<void> {
      try {
        setLoading(true);
        setError(null);

        // Fetch account info and event registrations in parallel
        const [accountData, eventsData] = await Promise.all([
          client.request<{ account: AccountInfo }>('GET', '/account'),
          client.request<{ events: ParticipantEvent[] }>('GET', '/account/events'),
        ]);

        if (cancelled) return;

        setAccount(accountData.account);

        const events = eventsData.events;
        // Find the current active/published event (prefer active, then published)
        const activeEvent = events.find((e) => e.eventStatus === 'active');
        const publishedEvent = events.find((e) => e.eventStatus === 'published');
        const event = activeEvent ?? publishedEvent ?? events[0] ?? null;

        setCurrentEvent(event);

        // Generate and cache the team check-in QR code
        if (event?.eventScopedTeamId) {
          const encoded = encodeTeamCheckInQR({
            e: event.eventId,
            t: event.eventScopedTeamId,
          });
          setQrValue(encoded);
          // Cache for offline display (Req 18.1)
          try {
            localStorage.setItem(QR_CACHE_KEY, encoded);
          } catch {
            // localStorage may be unavailable; non-critical
          }
        } else {
          setQrValue(null);
        }
      } catch (err) {
        if (cancelled) return;
        // Attempt to load cached QR for offline display
        try {
          const cached = localStorage.getItem(QR_CACHE_KEY);
          if (cached) {
            setQrValue(cached);
          }
        } catch {
          // ignore
        }
        setError(
          err instanceof Error ? err.message : 'Failed to load dashboard data',
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void fetchDashboardData(api);

    return () => {
      cancelled = true;
    };
  }, [api]);

  return (
    <AppLayout title="Dashboard" footer={<DonationButton />}>
      <section aria-label="Participant information">
        <h2 style={{ fontSize: 'var(--font-size-heading-sm, 1.25rem)', marginBottom: '0.5rem' }}>
          Welcome, {account?.name ?? user?.email ?? 'Participant'}
        </h2>

        {loading && <p>Loading event data…</p>}

        {error && !loading && (
          <p role="alert" style={{ color: 'var(--color-brand-red, #DC2F02)' }}>
            {error}
          </p>
        )}

        {!loading && currentEvent && (
          <div
            style={{
              border: '1px solid var(--color-border, #E5E7EB)',
              borderRadius: 'var(--radius-card, 12px)',
              padding: '1rem',
              marginBottom: '1.5rem',
            }}
          >
            <h3 style={{ margin: '0 0 0.5rem', fontSize: 'var(--font-size-heading-sm, 1.25rem)' }}>
              {currentEvent.eventName}
            </h3>
            <dl style={{ margin: 0, display: 'grid', gap: '0.25rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <dt style={{ fontWeight: 600 }}>Date:</dt>
                <dd style={{ margin: 0 }}>{currentEvent.eventDate}</dd>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <dt style={{ fontWeight: 600 }}>Status:</dt>
                <dd style={{ margin: 0 }}>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '0.125rem 0.5rem',
                      borderRadius: 'var(--radius-button, 8px)',
                      fontSize: 'var(--font-size-body-sm, 0.875rem)',
                      fontWeight: 500,
                      backgroundColor:
                        currentEvent.eventStatus === 'active'
                          ? '#e6f4ea'
                          : '#f0f0f0',
                      color:
                        currentEvent.eventStatus === 'active'
                          ? 'var(--color-brand-green, #1B4332)'
                          : 'var(--color-text-secondary, #6B7280)',
                    }}
                  >
                    {currentEvent.eventStatus}
                  </span>
                </dd>
              </div>
              {currentEvent.teamName && (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <dt style={{ fontWeight: 600 }}>Team:</dt>
                  <dd style={{ margin: 0 }}>{currentEvent.teamName}</dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {!loading && !currentEvent && !error && (
          <p style={{ color: 'var(--color-text-secondary, #6B7280)' }}>
            No active event found. Check your{' '}
            <Link to="/invitations">invitations</Link> for pending events.
          </p>
        )}
      </section>

      {/* Team Check-In QR Code Display (Req 13.5, 13.6, 18.1) */}
      {qrValue && (
        <section
          aria-label="Team check-in QR code"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            border: '1px solid var(--color-border, #E5E7EB)',
            borderRadius: 'var(--radius-card, 12px)',
            backgroundColor: 'var(--color-bg-surface, #FFFFFF)',
          }}
        >
          <h3 style={{ margin: '0 0 0.75rem', fontSize: 'var(--font-size-body-md, 1rem)' }}>
            Team Check-In QR
          </h3>
          <div
            style={{
              padding: '1rem',
              backgroundColor: 'var(--color-bg-surface, #FFFFFF)',
              borderRadius: 'var(--radius-button, 8px)',
            }}
          >
            <QRCodeSVG
              value={qrValue}
              size={200}
              level="M"
              includeMargin
              aria-label={`QR code for team ${currentEvent?.teamName ?? 'check-in'}`}
            />
          </div>
          {currentEvent?.teamName && (
            <p
              style={{
                marginTop: '0.5rem',
                fontSize: 'var(--font-size-body-sm, 0.875rem)',
                color: 'var(--color-text-secondary, #6B7280)',
              }}
            >
              Show this to the score taker at your station
            </p>
          )}
        </section>
      )}

      {/* Navigation Links */}
      <nav aria-label="Participant navigation">
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'grid',
            gap: '0.75rem',
          }}
        >
          <li>
            <Link
              to="/scores"
              style={{
                display: 'block',
                padding: '0.75rem 1rem',
                border: '1px solid var(--color-border, #E5E7EB)',
                borderRadius: 'var(--radius-card, 12px)',
                textDecoration: 'none',
                color: 'var(--color-brand-green, #1B4332)',
                fontWeight: 500,
              }}
            >
              My Scores
            </Link>
          </li>
          <li>
            <Link
              to="/leaderboard"
              style={{
                display: 'block',
                padding: '0.75rem 1rem',
                border: '1px solid var(--color-border, #E5E7EB)',
                borderRadius: 'var(--radius-card, 12px)',
                textDecoration: 'none',
                color: 'var(--color-brand-green, #1B4332)',
                fontWeight: 500,
              }}
            >
              Leaderboard
            </Link>
          </li>
          <li>
            <Link
              to="/invitations"
              style={{
                display: 'block',
                padding: '0.75rem 1rem',
                border: '1px solid var(--color-border, #E5E7EB)',
                borderRadius: 'var(--radius-card, 12px)',
                textDecoration: 'none',
                color: 'var(--color-brand-green, #1B4332)',
                fontWeight: 500,
              }}
            >
              Invitations
            </Link>
          </li>
          <li>
            <Link
              to="/stations"
              style={{
                display: 'block',
                padding: '0.75rem 1rem',
                border: '1px solid var(--color-border, #E5E7EB)',
                borderRadius: 'var(--radius-card, 12px)',
                textDecoration: 'none',
                color: 'var(--color-brand-green, #1B4332)',
                fontWeight: 500,
              }}
            >
              Stations
            </Link>
          </li>
          <li>
            <Link
              to="/scan"
              style={{
                display: 'block',
                padding: '0.75rem 1rem',
                border: '1px solid var(--color-border, #E5E7EB)',
                borderRadius: 'var(--radius-card, 12px)',
                textDecoration: 'none',
                color: 'var(--color-brand-green, #1B4332)',
                fontWeight: 500,
              }}
            >
              Scan QR Code
            </Link>
          </li>
        </ul>
      </nav>
    </AppLayout>
  );
}
